import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { useAuthContext } from '@/providers/AuthProvider';
import { krBankName } from '@/lib/domain/krBanks';
import { minorToMajorString } from '@/features/orders/lib/chargeMoney';
import { usePaymentsQuery } from '@/features/payments/hooks/usePaymentsQuery';
import { usePaymentQuery } from '@/features/payments/hooks/usePaymentQuery';
import { useManualRefundMutation, useRefundMutation } from '@/features/payments/hooks/usePaymentActionMutation';
import { ManualRefundModal } from '@/features/payments/components/ManualRefundModal';
import { RefundActionModal } from '@/features/payments/components/RefundActionModal';
import { claimRefundAccountPrefill } from '@/features/payments/lib/claimRefundAccount';
import { refundFlagsOf } from '@/features/payments/lib/refundFlags';
import { canPerformPaymentAction } from '@/features/payments/lib/paymentActionRbac';
import { claimQueryKey } from '../hooks/useClaimQuery';

/** 클레임 상세 refundAccount(order-service a7dd5f8). BO 응답은 원문(masked=false). */
interface ClaimRefundAccountView {
  bankCode?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  accountHolderName?: string | null;
  masked?: boolean | null;
}

interface ClaimForRefund {
  claimId: string;
  orderId: string;
  claimStatus?: string | null;
  expectedRefundAmount?: { amount: number; currencyCode: string } | null;
  refundAccount?: ClaimRefundAccountView | null;
}

interface ClaimRefundPaymentLinksProps {
  claim: ClaimForRefund & Record<string, unknown>;
}

/** 화면 기본 표시는 끝 4자리만 — 계좌이체에 쓸 원문은 [보기]로 연다. */
const maskAccount = (v: string): string => (v.length <= 4 ? v : `${'*'.repeat(Math.max(0, v.length - 4))}${v.slice(-4)}`);

/**
 * 클레임 상세 — 고객 환불 계좌 + 환불 진입.
 *
 * - 고객 환불 계좌: 은행(코드가 있으면 은행 목록 이름, 없으면 bankName) · 계좌번호(마스킹, [보기]로 원문) · 예금주.
 *   고도몰 이관 클레임은 계좌번호가 이관되지 않아 null 일 수 있다 → "계좌번호 없음 — 고객에게 확인 필요".
 * - 결제: 이 주문의 결제(목록 keyword 는 orderId 부분일치라 같은 orderId 만)로 가는 링크(?claimId → 환불 모달이 계좌를 채운다).
 * - 클레임이 환불 대기(REFUND_PENDING)이고 결제가 수기 환불 가능(이관·수기주문·Toss 가상계좌)이면 [수기 환불 기록]을
 *   여기서 바로 연다 — claimId · 예상 환불액 · 고객 계좌를 채워서. 기록하면 BE 이벤트로 클레임이 환불 완료가 된다(수 초 지연).
 * - QA-558 PG 결제(카드·간편결제 등)는 [환불 처리(PG 취소)]로 결제 상세와 같은 환불을 여기서 바로 한다(예상 환불액으로
 *   채움). PG 취소가 끝나면 payment.refund.completed 이벤트로 클레임이 환불 완료가 된다 — 결제 화면에 갈 필요가 없다.
 */
export const ClaimRefundPaymentLinks = ({ claim }: ClaimRefundPaymentLinksProps) => {
  const { t } = useTranslation('claims');
  const qc = useQueryClient();
  const { currentAdmin } = useAuthContext();
  const [revealed, setRevealed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [pgOpen, setPgOpen] = useState(false);
  const [pgRequested, setPgRequested] = useState(false);
  const pgRefund = useRefundMutation();

  const paymentsQuery = usePaymentsQuery({ keyword: claim.orderId, size: 10 }, { enabled: Boolean(claim.orderId) });
  const payments = (paymentsQuery.data?.items ?? []).filter((p) => p.orderId === claim.orderId);
  // 수기 환불 가능 여부·남은 금액은 상세에만 있다 — 첫 결제(보통 1건)의 상세를 읽는다.
  const firstPaymentId = payments[0]?.paymentId;
  const paymentQuery = usePaymentQuery(claim.claimStatus === 'REFUND_PENDING' ? firstPaymentId : undefined);
  const manualRefund = useManualRefundMutation();
  // 모달은 이 값이 바뀌면 폼을 다시 채우므로, 렌더마다 새 객체가 되지 않게 고정한다.
  const prefill = useMemo(() => claimRefundAccountPrefill(claim), [claim]);

  const account = claim.refundAccount && !claim.refundAccount.masked ? claim.refundAccount : null;
  const accountNumber = account?.accountNumber?.trim() ?? '';
  const bankLabel = krBankName(account?.bankCode) ?? account?.bankName ?? account?.bankCode ?? null;

  const payment = paymentQuery.data;
  const flags = payment ? refundFlagsOf(payment) : null;
  const refundable =
    payment != null &&
    (payment.remainingRefundableAmount ?? 0) > 0 &&
    (payment.paymentStatus === 'CAPTURED' || payment.paymentStatus === 'PARTIALLY_REFUNDED');
  const canManual =
    claim.claimStatus === 'REFUND_PENDING' &&
    flags?.manualAvailable === true &&
    canPerformPaymentAction('refund', currentAdmin?.roles ?? []);
  // QA-558 PG 환불 — 수기 전용(이관·수기주문)·고도몰에서 이미 PG 취소된 결제는 제외.
  const canPg =
    claim.claimStatus === 'REFUND_PENDING' &&
    flags != null &&
    !flags.manualOnly &&
    !flags.pgCancelled &&
    canPerformPaymentAction('refund', currentAdmin?.roles ?? []);
  const expected = claim.expectedRefundAmount;
  const defaultAmountStr =
    expected && payment && expected.currencyCode === payment.currencyCode
      ? minorToMajorString(expected.amount, expected.currencyCode)
      : null;

  // QA-558(BE 회신) — 첫 환불 완료로 클레임이 바로 완료된다(부분 완료 없음). 예상 환불액과 다른 금액이면 한 번 확인한다.
  const confirmAmount = (amount: number | null | undefined): boolean => {
    const expectedMajor = defaultAmountStr != null ? Number(defaultAmountStr) : null;
    if (expectedMajor == null || amount == null || amount === expectedMajor) return true;
    return window.confirm(
      t('claims.detail.refundAmountMismatch', {
        amount: amount.toLocaleString(),
        expected: expectedMajor.toLocaleString(),
        defaultValue:
          '예상 환불액({{expected}})과 다른 금액({{amount}})입니다. 이 금액으로 환불하면 클레임이 바로 환불 완료가 되고, 이후 같은 주문의 추가 환불은 클레임에 연결되지 않습니다. 진행할까요?',
      }),
    );
  };

  if (payments.length === 0 && !account) return null;

  return (
    <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }} data-testid="claim-refund-payment">
      <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
        {t('claims.detail.refundPayment', '환불 결제')}
      </h3>

      {account ? (
        <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm" data-sentry-mask>
          <span className="text-[color:var(--content-muted)]">{t('claims.detail.refundAccount', '고객 환불 계좌')}</span>
          <span className="text-[color:var(--content-strong)]" data-testid="claim-refund-account">
            {[bankLabel, accountNumber ? (revealed ? accountNumber : maskAccount(accountNumber)) : null, account.accountHolderName]
              .filter(Boolean)
              .join(' · ')}
            {accountNumber ? (
              <button
                type="button"
                className="ml-2 text-xs underline"
                style={{ color: 'var(--content-muted)' }}
                onClick={() => setRevealed((v) => !v)}
                data-testid="claim-refund-account-reveal"
              >
                {revealed ? t('claims.detail.refundAccountHide', '가리기') : t('claims.detail.refundAccountShow', '보기')}
              </button>
            ) : null}
          </span>
          {!accountNumber ? (
            <span className="col-span-2 text-xs" style={{ color: 'var(--status-warn-fg)' }} data-testid="claim-refund-account-missing">
              {t('claims.detail.refundAccountMissing', '계좌번호 없음 — 고객에게 확인 필요')}
            </span>
          ) : null}
        </div>
      ) : null}

      {canPg && payment ? (
        <div className="mb-2">
          <Button
            type="button"
            size="sm"
            variant="cta"
            disabled={!refundable}
            onClick={() => {
              setPgRequested(false);
              pgRefund.reset();
              setPgOpen(true);
            }}
            data-testid="claim-pg-refund"
          >
            {t('claims.detail.pgRefund', '환불 처리(PG 취소)')}
          </Button>
          {!refundable ? (
            <p className="mt-1 text-xs text-[color:var(--content-muted)]">
              {t('claims.detail.pgRefundNone', '이 결제는 환불할 금액이 남아 있지 않습니다.')}
            </p>
          ) : null}
        </div>
      ) : null}

      {pgRequested ? (
        <p
          role="status"
          className="mb-2 rounded px-2 py-1.5 text-xs"
          style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
          data-testid="claim-pg-refund-requested"
        >
          {t(
            'claims.detail.pgRefundRequested',
            'PG 환불을 요청했습니다. 취소가 끝나면 클레임이 환불 완료로 바뀝니다(수 초). 잠시 뒤 새로고침해 주세요.',
          )}
        </p>
      ) : null}

      {canManual && payment ? (
        <div className="mb-2">
          <Button
            type="button"
            size="sm"
            variant="danger"
            disabled={!refundable || flags?.pgCancelled === true}
            onClick={() => {
              setRecorded(false);
              setModalOpen(true);
            }}
            data-testid="claim-manual-refund"
          >
            {flags?.manualOnly
              ? t('claims.detail.manualRefund', '수기 환불 기록')
              : t('claims.detail.manualRefundTransfer', '수기 환불 기록(계좌이체)')}
          </Button>
          {flags?.pgCancelled ? (
            <p className="mt-1 text-xs" style={{ color: 'var(--status-danger-fg)' }}>
              {t('claims.detail.pgCancelled', '고도몰에서 이미 PG 취소된 결제입니다. 중복 환불하지 마세요.')}
            </p>
          ) : null}
        </div>
      ) : null}

      {recorded ? (
        <p
          role="status"
          className="mb-2 rounded px-2 py-1.5 text-xs"
          style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
          data-testid="claim-manual-refund-recorded"
        >
          {t(
            'claims.detail.manualRefundRecorded',
            '환불을 기록했습니다. 클레임은 수 초 안에 환불 완료로 바뀝니다. 잠시 뒤 새로고침해 주세요.',
          )}
        </p>
      ) : null}

      {payments.map((p) => (
        <Link
          key={p.paymentId}
          to={`/payments/${p.paymentId}?claimId=${encodeURIComponent(claim.claimId)}`}
          className="block text-sm font-medium text-[color:var(--content-strong)] underline"
          data-testid="claim-refund-payment-link"
        >
          {t('claims.detail.goRefund', '결제 상세에서 환불하기')}
        </Link>
      ))}

      {canPg && payment ? (
        <RefundActionModal
          payment={payment}
          isOpen={pgOpen}
          isSubmitting={pgRefund.isPending}
          errorMessage={pgRefund.error ? friendlyMessage(pgRefund.error, '환불에 실패했습니다.') : null}
          prefillRefundAccount={prefill}
          defaultAmount={defaultAmountStr != null ? Number(defaultAmountStr) : null}
          onSubmit={(body) =>
            confirmAmount(body.amount) &&
            pgRefund.mutate(
              { paymentId: payment.paymentId, body },
              {
                onSuccess: () => {
                  setPgOpen(false);
                  setPgRequested(true);
                  void qc.invalidateQueries({ queryKey: claimQueryKey(claim.claimId) });
                  void qc.invalidateQueries({ queryKey: ['admin', 'claims'] });
                },
              },
            )
          }
          onCancel={() => setPgOpen(false)}
        />
      ) : null}

      {canManual && payment && flags ? (
        <ManualRefundModal
          payment={payment}
          isOpen={modalOpen}
          isSubmitting={manualRefund.isPending}
          errorMessage={manualRefund.error ? friendlyMessage(manualRefund.error, '환불 기록에 실패했습니다.') : null}
          manualRefundOnly={flags.manualOnly}
          legacyPg={flags.legacyPg}
          prefillRefundAccount={prefill}
          claimId={claim.claimId}
          defaultAmount={defaultAmountStr != null ? Number(defaultAmountStr) : null}
          onSubmit={(body, idempotencyKey) =>
            confirmAmount(body.amount) &&
            manualRefund.mutate(
              { paymentId: payment.paymentId, body, idempotencyKey },
              {
                onSuccess: () => {
                  setModalOpen(false);
                  setRecorded(true);
                  void qc.invalidateQueries({ queryKey: claimQueryKey(claim.claimId) });
                },
              },
            )
          }
          onCancel={() => setModalOpen(false)}
        />
      ) : null}
    </div>
  );
};
